import React, { useEffect, useRef } from "react";
import { Tooltip } from "bootstrap";
import Moment from "moment";
import DOMPurify from "dompurify";
import he from "he";
import axios from "axios";
import { serverGet } from "../../middleware/http";
import { apiConst } from "../constant/constant";
import { clearFlowCache } from "./requestCache";

export const errorMessages = {
    messagesCache: null,
    isInitialized: false,
    initPromise: null,

    async initialize() {
        if (this.isInitialized) return;

        if (this.initPromise) return this.initPromise;

        const cachedMessages = localStorage.getItem("errorMsg");
        if (cachedMessages) {
            try {
                this.messagesCache = JSON.parse(cachedMessages);
                this.isInitialized = true;
                return;
            } catch (err) {
                console.warn(err);
            }
        }

        this.initPromise = axios
            .get(apiConst.getresponsecode + "/en-us")
            .then(result => {
                const data = result?.data?.data;
                if (Array.isArray(data) && result?.data?.status == 1) {
                    this.messagesCache = data;
                    localStorage.setItem("errorMsg", JSON.stringify(data));
                    this.isInitialized = true;
                } else {
                    console.warn(result);
                }
            })
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                this.initPromise = null;
            });

        await this.initPromise;
    },

    getMessage(code) {
        if (!this.isInitialized) return null;
        if (!this.messagesCache) return null;

        const message = this.messagesCache.find(m => m?.code == code);
        return message?.description || null;
    },
};

export const getMsg = (code) => {
    if (!code) return "Validation error";

    let eCode = parseInt(code);
    const message = errorMessages.getMessage(eCode);

    return message || "Validations error";
};

const validationPatterns = {
    email: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i,
    password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!$%?*.@#])[^\s]{8,}$/,
    alphanumeric: /^[a-zA-Z0-9_]*$/,
    alphabet: /^[a-zA-Z\s]*$/,
    number: /^[0-9]+$/,
    decimalnumbers: /^-?\d+(\.\d{1,2})?$/,
    time: /^(0[1-9]|1[0-2]):([0-5][0-9]) ((a|p)m|(A|P)M)$/,
    phonenumber: /^\d{10}$/,
    alphanumericspcl: /^[a-zA-Z0-9\s]+$/,
    feet: /^[0-8]$/,
    inch: /^(?:0|[1-9]|10|11)$/,
    filename_val: /^[^\/:*?"<>|#]*$/,
    uszipcode: /^\d{5}(-\d{4})?$/
};

export let formValidation = null;

export function buildFormValidation() {
    return {
        email: {
            pattern: {
                value: validationPatterns.email,
                message: getMsg(204),
            },
        },
        password: {
            pattern: {
                value: validationPatterns.password,
                message: getMsg(314),
            },
        },
        invalidPassword: {
            pattern: {
                value: validationPatterns.password,
                message: getMsg(218),
            },
        },
        passwordwithdescription: {
            pattern: {
                value: validationPatterns.password,
                message: getMsg(496),
            },
        },
        alphanumeric: {
            pattern: {
                value: validationPatterns.alphanumeric,
                message: getMsg(313),
            },
        },
        alphabet: {
            pattern: {
                value: validationPatterns.alphabet,
                message: getMsg(301),
            },
        },
        number: {
            pattern: {
                value: validationPatterns.number,
                message: getMsg(317),
            },
        },
        decimalnumbers: {
            pattern: {
                value: validationPatterns.decimalnumbers,
                message: getMsg(377),
            },
        },
        time: {
            pattern: {
                value: validationPatterns.time,
                message: getMsg(321),
            },
        },
        phonenumber: {
            pattern: {
                value: validationPatterns.phonenumber,
                message: getMsg(215),
            },
        },
        alphanumericspcl: {
            pattern: {
                value: validationPatterns.alphanumericspcl,
                message: getMsg(369),
            },
        },
        uszipcode: {
            pattern: {
                value: validationPatterns.uszipcode,
                message: getMsg(820),
            },
        },
        feet: {
            pattern: { value: validationPatterns.feet, message: getMsg(371) },
        },
        inch: {
            pattern: { value: validationPatterns.inch, message: getMsg(371) },
        },
        validateNoEmptySpaces: (value) =>
            String(value)?.trim() === "" ? getMsg(370) : true,

        filename_val: validationPatterns.filename_val,
    };
}


export async function initValidation() {
    await errorMessages.initialize();
    formValidation = buildFormValidation();
}

export const getMemberId = async () => {
    try {
        let member_id = localStorage.getItem("memberId");
        if (!member_id) {
            const patient_res = await serverGet(apiConst.patientinfo);
            if (
                patient_res?.status == 1 &&
                patient_res?.data &&
                patient_res?.data?.memberId
            ) {
                member_id = patient_res?.data?.memberId;
                localStorage.setItem("memberId", member_id);
            }
        }
        return member_id;
    } catch (e) {
        console.error(e);
    }
};

export const cleanQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    let hasChanges = false;

    params.forEach((value, key) => {
        if (value === "null" || value === "undefined" || value === "") {
            params.delete(key);
            hasChanges = true;
        }
    });

    if (hasChanges) {
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, "", newUrl);
    }
};


export const isBoolean = (val) => "boolean" === typeof val;

export const checkIfValidUUID = (str) => {
    const regexExp =
        /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    return regexExp.test(str);
};


export const customValidateCheckbox = (data) => {
    return data != "" ? true : getMsg(319);
};


export const redirectPageNotFound = () => {
    return (window.location.href = "/pagenotfound");
};

export const customValidate = (watch, name, message, condition) => {
    let watchField = "";
    if (name == "") {
        watchField = watch();
        const data = watchField[condition.source];
        const filterData = data.filter(
            (em) => em[condition.compareFrom] === em[condition.compareTo]
        );
        return filterData.length > 0 ? true : message;
    } else {
        watchField = watch(name);
        return watchField.length > 0 ? true : message;
    }
};

export const updateHistory = (name, url) => {

    let sessionObj = {};
    const storedSession = sessionStorage.getItem("sessionUrls");
    if (storedSession) {
        sessionObj = JSON.parse(storedSession);
    }

    if (name !== "clear" && url && name?.slice(1)) {
        sessionObj[name?.slice(1)] = url;
        sessionStorage.setItem("sessionUrls", JSON.stringify(sessionObj));
    } else if (name === "clear") {
        sessionStorage.removeItem("sessionUrls");
    }

    return sessionObj;
};

export const ecnSession = (value, name) => {
    let sessionObj = {};

    // Retrieve the session data from localStorage
    const storedSession = sessionStorage.getItem("sessionData");

    if (storedSession) {
        sessionObj = JSON.parse(storedSession);
    }

    if (value !== "clear" && value) {
        sessionObj[name] = value;
        sessionStorage.setItem("sessionData", JSON.stringify(sessionObj));
    } else if (value === "clear") {
        sessionStorage.removeItem("sessionData");
        clearFlowCache();
    }

    return sessionObj;
};

// ecnSession only writes truthy values and its "clear" path drops the whole session object, so there is
// no way to retract a single key through it. Use this when a key must be removed rather than replaced.
export const removeEcnSession = (name) => {
    const storedSession = sessionStorage.getItem("sessionData");
    if (!storedSession) return {};

    const sessionObj = JSON.parse(storedSession);
    delete sessionObj[name];
    sessionStorage.setItem("sessionData", JSON.stringify(sessionObj));

    return sessionObj;
};

// The protocol gate's answer is a per-speciality verdict, so changing speciality invalidates it the
// same way it invalidates the provider and the slot. Nothing re-derives these on the way through: a
// speciality with protocol switched off never reaches Service.js's applyProtocolDecision, the only
// code that rewrites them, so a useExistingLabReport left over from a protocol-enabled speciality
// kept the Reason page showing the upload banner and a required Lab Report under a speciality that
// has no protocol at all, and protocolDecisionFor told the gate a decision was already on file so it
// never re-asked for the new one. Called from every place that moves spid, not just the speciality
// step -- a service with exactly one speciality sets it straight from Service.js and skips that step.
// autoAttachLabReportsByScope is deliberately left alone: it is keyed by speciality and patient and
// is what lets switching back restore the right list.
// Everything chosen UNDER a service/speciality, in URL-param form. A reason, a provider and slot, a
// pharmacy and a duration all stop describing the booking the moment the speciality changes -- but
// they live in the URL, which survives the change, and DoctorSummary renders straight off these keys.
// That is why the sidebar kept naming the previous reason, doctor and pharmacy after switching.
// eid and schid are deliberately NOT here: the booking itself continues, and the intake sync rewrites
// it on the next submit. Nor are the patient-scoped ones (fmid, rsid, primemid), which outlive any
// speciality.
export const specialityScopedParams = [
    // REC
    "rid", "r", "reascode", "rnote", "speicalityservice", "medexcuselet", "discountId",
    // PHS
    "p", "phaid", "systemphaid", "phalist", "pharmacyStateId", "pharmacysearch", "postalcode",
    // provider, slot and the encounter shape chosen with them
    "memberid", "ts", "ad", "et", "did", "dn", "emid", "m", "snum", "selectedd", "kwd", "minP", "week", "nwpok",
];

// The session half of the same idea. Pricing is resolved per speciality and service option, so a
// discount or an amount carried over describes a consultation the patient is no longer booking --
// DoctorSummary renders that amount. The protocol keys are the verdict for the speciality that was
// asked about.
// uploadid is deliberately absent: it is one shared staging list for every uploader in the app
// (insurance, identity proof and the reason documents all append to it), so it cannot be dropped on a
// speciality change without taking the patient's identity documents with it.
export const specialityScopedSessionKeys = [
    "useExistingLabReport", "protocolDecisionFor", "specialityProtocol", "autoAttachLabReports",
    "discountId", "discountCategoryId", "discountCode", "discountPrice",
    "apiDiscountCouponCode", "apiDiscountValue",
    "price", "originalPrice", "finalPrice", "benefitPrice", "medicinePrice", "refillPrice",
];

// Invalid on top of the above when the SERVICE changes: the step machine is built from the service's
// own workflow, and the plan, subscription and in-progress payment were all priced against it.
export const serviceScopedSessionKeys = [
    "intakeModules", "serviceFlowHistory", "prunedStepCount", "workFlowService", "workFlowServiceId",
    "planId", "planDetailId", "memberPlanId", "groupPlanId", "billingCycle", "subscriptionId",
    "enrollmentId", "enrollmentOrderId", "itemId",
    "payMode", "payType", "paymentType", "paymentURL", "transactionId", "orderID",
    "encounterInsuranceId", "refill", "isFormExit",
];

export const clearSpecialityScopedState = (nextSpecialityId) => {
    const _current = getSearchParams("spid");
    if (!_current || _current === nextSpecialityId) return;

    specialityScopedParams.forEach((key) => removeSearchParam(key));
    specialityScopedSessionKeys.forEach((key) => removeEcnSession(key));

    // Belongs to a lab order raised under the previous speciality -- Reason reads it to load that
    // order's result document and to treat the requirement as already met.
    removeSearchParam("labrxId");

    // The encounter still carries the PREVIOUS speciality's EncounterTemplateId until the intake
    // sync rewrites it, and that column -- not the sid on the request -- is what dcEncounterForm_Get
    // resolves its form list from. So every encounter-scoped form read answers for the old speciality
    // until the next submit, however new the speciality on the URL is. Flagged here so the consent
    // load can ask the speciality instead for that window; cleared by the submit that rebuilds it.
    if (getSearchParams("eid")) ecnSession(true, "encounterTemplateStale");
};

// Whether the chosen speciality positively has the protocol gate switched OFF. The lab-report
// requirement belongs to that gate, so a speciality without it must not ask for one -- an existing
// upload is only evidence that some EARLIER speciality wanted a report, never that this one does.
//
// Deliberately not the inverse of "enabled": the list is written by the two speciality paths, and an
// entry that arrives with spid already resolved (SSO/ext) may never have stored one. This answer is
// used to suppress a requirement, so a speciality that is simply not on file has to read as "cannot
// say" and leave the existing behaviour alone rather than silently drop a real obligation.
export const isSpecialityProtocolDisabled = (specialityId) => {
    const _specialityList = ecnSession()?.specialityList;
    if (!isArray(_specialityList) || !specialityId) return false;

    const _speciality = _specialityList.find((s) => s.id?.toLowerCase() === specialityId.toLowerCase());
    return !!_speciality && !_speciality.isEnableProtocol;
};

// The strict opposite question, and not interchangeable with the above: this one is false when the
// speciality is not on file. Used to decide whether the eligibility read is worth warming, where the
// cost of being wrong is one wasted round trip rather than a dropped requirement.
export const isSpecialityProtocolEnabled = (specialityId) => {
    const _specialityList = ecnSession()?.specialityList;
    return isArray(_specialityList) && _specialityList.some((s) => s.id?.toLowerCase() === specialityId?.toLowerCase() && s.isEnableProtocol);
};

export const labSession = (value, name) => {
    let sessionObj = {};
    const stored = sessionStorage.getItem("labOrderData");
    if (stored) sessionObj = JSON.parse(stored);
    if (value === "clear") {
        sessionStorage.removeItem("labOrderData");
        sessionStorage.removeItem("pendingLabOrder");
        return {};
    }
    if (value !== undefined && name) {
        sessionObj[name] = value;
        sessionStorage.setItem("labOrderData", JSON.stringify(sessionObj));
    }
    return sessionObj;
};

export const enrollSession = (value, name) => {
    let sessionObj = {};

    // Retrieve the session data from localStorage
    const storedSession = sessionStorage.getItem("pendingEnrollment");

    if (storedSession) {
        sessionObj = JSON.parse(storedSession);
    }

    if (value !== "clear" && value) {
        sessionObj[name] = value;
        sessionStorage.setItem("pendingEnrollment", JSON.stringify(sessionObj));
    } else if (value === "clear") {
        sessionStorage.removeItem("pendingEnrollment");
    }

    return sessionObj;
};
export const authSessionExpiry = {
    mark: () => {
        sessionStorage.setItem("authSessionExpired", "1");
    },
    clear: () => {
        sessionStorage.removeItem("authSessionExpired");
    },
    isExpired: () => sessionStorage.getItem("authSessionExpired") === "1",
};

export const getAuthProfile = {
    saveProfileAuth: () => {
        sessionStorage.setItem("isAuth", true);
    },
    clearProfileAuth: () => {
        sessionStorage.removeItem("isAuth");
    },
    getProfileAuth: () => {
        const _temp = sessionStorage.getItem("isAuth");
        if (!_temp || _temp == "false") return false;

        return sessionStorage.getItem("isAuth");
    },
};

export const combineDateTime = (dateStr, timeStr) => {
    const formattedDate = Moment(
        dateStr,
        "ddd MMM DD YYYY HH:mm:ss [GMT] Z (zz)"
    ).format("MM-DD-YYYY");
    const date = Moment(formattedDate, "MM-DD-YYYY");
    const time = Moment(timeStr, "hh:mm A");
    const combined_DateTime = date.clone().set({
        hour: time.hours(),
        minute: time.minutes(),
    });

    //const formattedDateTime = combinedDateTime.format('MM-DD-YYYY hh:mm A');

    const formattedDateTime = combined_DateTime.toDate();

    // console.log(formattedDateTime);

    return formattedDateTime;
};

//TO FORMAT UTC DATE & TIME

export const dateTimeFormat = (value, format) => {
    if (!value) return;
    let date_format = format;

    if (!date_format || date_format == "") {
        date_format = "MM-DD-YYYY";
    }

    const convertDatetime = (datetime) => {
        const date = new Date(datetime + "Z");
        return Moment(new Date(date)).format(`${date_format}`);
    };
    return convertDatetime(value);
};

export const dateAndTime = (value, format) => {
    let date_format = format;

    if (!date_format || date_format == "") {
        date_format = "MM-DD-YYYY";
    }

    const convertDatetime = (datetime) => {
        const date = new Date(datetime + "Z");

        const momentDate = Moment(new Date(date));

        if (momentDate.isSame(Moment(), "day")) {
            return momentDate.format("hh:mm A"); // Return time format
        } else {
            return momentDate.format(date_format); // Return date format
        }
    };
    return convertDatetime(value);
};

//TO FORMAT UTC TIME

export const timeFormat = (value, format) => {
    if (!value) return;
    const dateFormat = format ? format : "hh:mm A";
    const time = value?.split("+")[0];
    const convertDatetime = (datetime) => {
        const date = new Date(datetime + "Z");
        return Moment(new Date(date)).format(dateFormat);
    };

    return convertDatetime(time);
};

//TO FORMAT DATE & TIME

export const dateFormat = (value, format) => {
    if (!value) return;
    let date_format = format;

    if (!date_format || date_format == "") {
        date_format = "MM-DD-YYYY";
    }

    return Moment(value).format(date_format);
};

export const getUtcTimeNow = () => {
    return Moment.utc(new Date()).format("YYYY-MM-DDTHH:mm:ss");
};

// Start/end of "today" in the browser's local time, converted to UTC — used for the lab order
// provider round-robin window (see LabOrderPaymentRequest.FromDate/ToDate).
export const getTodayDateRange = () => {
    return {
        fromDate: Moment().startOf("day").utc().format("YYYY-MM-DDTHH:mm:ss"),
        toDate: Moment().endOf("day").utc().format("YYYY-MM-DDTHH:mm:ss"),
    };
};

//export const dateFormat = (value, format) => {

//    return Moment(value).format(format);
//}
export const getAge = (d1, d2) => {
    if (!d1) return;

    let Date1 = d1;
    let Date2 = d2;

    if (Date1) {
        Date1 = new Date(Moment(Date1, "YYYY-MM-DD"));
    }

    if (Date2) {
        Date2 = new Date(Moment(Date2, "YYYY-MM-DD"));
    } else {
        Date2 = new Date();
    }

    var diff = Date2.getTime() - Date1.getTime();
    return Math.floor(Math.abs(diff / (1000 * 60 * 60 * 24 * 365.25)));
};
//console.log(getAge(new Date(1978, 10, 3)));

export const getBadgeColor = (value) => {
    switch (value?.toLowerCase()) {
        case "new":
            return "info";
        case "scheduled":
            return "primary";
        case "in progress":
            return "secondary";
        case "completed":
            return "success";
        case "ordered":
            return "success";
        case "active":
            return "primary";
        case 'draft':
            return 'brown';
        case 'pending':
            return 'warning';
        case 'order placed':
            return 'primary';
        case 'order confirmed':
            return 'success';
        case 'processing':
            return 'warning';
        case 'pending review':
            return 'warning';
        case 'ordered':
            return 'success';
        default:
            return "danger";
    }
};

export const getStatusBadgeColor = (value) => {
    switch (value?.toLowerCase()) {
        case "active":
            return "success";
        default:
            return "danger";
    }
};

export const getScheduleColor = (value) => {
    switch (value) {
        case 0:
            return "";
        case 1:
            return "theme-bg-blue";
        case 2:
            return "theme-bg-yellow";
        case 3:
            return "theme-bg-green";
        case 4:
            return "theme-bg-red";
        case 5:
            return "theme-bg-blue";
        case 6:
            return "theme-bg-brown";
        case 7:
            return "theme-bg-orange";
        case 8:
            return "theme-bg-purple";
        default:
            return "theme-bg-red";
    }
};

export const getEncounterColor = (value) => {
    switch (value) {
        case 0:
            return "";
        case 1:
            return "theme-bg-blue";
        case 2:
            return "theme-bg-yellow";
        case 3:
            return "theme-bg-green";
        case 4:
            return "theme-bg-red";
        case 5:
            return "theme-bg-brown";
        case 7:
            return "theme-bg-yellow";
        default:
            return "theme-bg-red";
    }
};


export const getStatusColor = (value) => {
    let _value;
    if (value) _value = value?.toString();
    switch (_value) {
        case "1":
            return "primary";
        case "2":
            return "danger";
        default:
            return "";
    }
};
export const groupBy = (xs, key) => {
    return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
    }, {});
};

export const isEnabled = (arr, code, value) => {
    return arr?.some((item) => item?.settingCode == code && item?.value == value);
};

export const isFeautureEnabled = (arr, code) => {
    return arr?.some(
        (item) => item?.featureCode == code && item?.id == item?.moduleFeatureId
    );
};

export const isUSAccount = (accountCountryRes) => accountCountryRes?.code === "US";

export const sanitizeZipCode = (value) => {
    const cleaned = String(value ?? "").replace(/[^0-9-]/g, "").replace(/^-+/, "");
    const [first, ...rest] = cleaned.split("-");
    return (rest.length ? `${first}-${rest.join("")}` : first).substring(0, 10);
};

export const getZipCodeRules = (isUS) =>
    isUS
        ? {
            required: getMsg(301),
            pattern: formValidation.uszipcode.pattern,
            validate: formValidation.validateNoEmptySpaces,
            minLength: { value: 5, message: getMsg(820) },
            maxLength: { value: 10, message: getMsg(820) },
        }
        : {
            required: getMsg(301),
            pattern: formValidation.alphanumericspcl.pattern,
            validate: formValidation.validateNoEmptySpaces,
            maxLength: { value: 10, message: getMsg("305") },
        };

export const isPartnerEnabled = (arr, code) => {
    return arr?.some(
        (item) => item?.code == code && item?.statusId == 1
    );
}

export const isModuleAssigned = (arr, id) => {
    return arr?.some((item) => item?.referenceId == id);
};

export const validateImageSize = (file, maxSizeInMB) => {
    const allowedSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (!file) {
        return 307;
    } else if (!file.type.startsWith("image/")) {
        return 307;
    } else if (file.size > allowedSizeInBytes) {
        return 306;
    } else {
        return "succeed";
    }
};

// TO UPDATE WINDOW SEARCH PARAMS
export const updateSearchParams = (name, value) => {
    if (value?.toString() && value?.toString().trim()) {
        const newUrl = new URL(window.location);
        newUrl.searchParams.set(name, value?.toString()?.trim());
        window.history.replaceState(null, "", newUrl);
        // replaceState is invisible to React Router's own location tracking (only its own
        // navigate()/Link calls update useLocation()), so a component reading a param via
        // getSearchParams() in its render body never re-renders just because this ran -- e.g. a
        // background write landing after the component that triggered it already unmounted (Reason.js's
        // pharmacy-assignment .then()). No-op for the many existing callers unless something listens.
        window.dispatchEvent(new CustomEvent("searchparamschange", { detail: { name } }));
    }
};

export const getSearchParams = (name) => {
    const urlSearchParams = new URLSearchParams(window.location.search);

    const value = urlSearchParams.get(name);

    if (value != "undefined" && value != "null" && value && value?.trim())
        return value?.trim();
};

export const removeSearchParam = (name) => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete(name);
    window.history.replaceState(null, "", newUrl);
    // See updateSearchParams -- same replaceState visibility gap for a param being cleared.
    window.dispatchEvent(new CustomEvent("searchparamschange", { detail: { name } }));
};

export const getHtmlContent = (data) => {
    const cleanHtml = DOMPurify.sanitize(data);
    const decodedHTML = he.decode(cleanHtml);

    return decodedHTML;
};

export const isArray = (array) => {
    return Array.isArray(array) && array?.length > 0;
};

export const hasKeys = (obj) =>
    obj != null && typeof obj === "object" && Object.keys(obj).length > 0;

export const splitCountryCode = (number, accIsdcode, list) => {
    const string = String(number).trim();
    const parts = string.split(" ");

    if (parts.length >= 2 && isArray(list)) {
        const [isdCode, phoneNumber] = parts;

        const isValidIsdCode = list?.some(
            (item) => item.isdCode === isdCode.trim()
        );
        const code = isValidIsdCode ? isdCode.trim() : accIsdcode;
        const phonenum = isValidIsdCode ? phoneNumber.trim() : number;

        return { isdCode: code, phoneNumber: phonenum.trim() };
    }
    const phoneNumber = string.trim();
    return { isdCode: accIsdcode, phoneNumber };
};

export const replaceQuilliClassToBootstrap = (data) => {
    var newHTML = data
        .replace(/class="ql-align-center"/g, 'class="text-center mb-0"')
        .replace(/class="ql-align-justify"/g, 'class="justify-content mb-0"')
        .replace(/class="ql-size-large"/g, 'class="card-title"')
        .replace(/class="ql-active"/g, 'class="active"')
        .replace(/class="ql-disabled"/g, 'class="disabled"')
        .replace(/class="ql-bold"/g, 'class="fw-bold"')
        .replace(/class="ql-italic"/g, 'class="fst-italic"')
        .replace(/class="ql-align-right"/g, 'class="text-end mb-0"')
        .replace(/class="ql-underline"/g, 'class="text-decoration-underline"')
        .replace(/<p><br><\/p>/g, '<p class="mb-0"><br/></p>')
        .replace(
            /<pre class="ql-syntax" spellcheck="false">/g,
            '<htmlcode class="ql-syntax" spellcheck="false">'
        )
        .replace(/<\/pre>/g, "</htmlcode>")
        .replace(/<p>/g, '<p class="mb-0">');
    return newHTML;
};

export const replaceBootstrapClassToQuilli = (data) => {
    var oldHTML = data
        .replace(/class="text-center"/g, 'class="ql-align-center"')
        .replace(/class="justify-content"/g, 'class="ql-align-justify"')
        .replace(/class="card-title"/g, 'class="ql-size-large"')
        .replace(/class="active"/g, 'class="ql-active"')
        .replace(/class="disabled"/g, 'class="ql-disabled"')
        .replace(/class="fw-bold"/g, 'class="ql-bold"')
        .replace(/class="fst-italic"/g, 'class="ql-italic"')
        .replace(/class="text-end"/g, 'class="ql-align-right"')
        .replace(/class="text-end mb-0"/g, 'class="ql-align-right"')
        .replace(/class="text-center mb-0"/g, 'class="ql-align-center"')
        .replace(/class="text-decoration-underline"/g, 'class="ql-underline"')
        .replace(
            /<htmlcode class="ql-syntax" spellcheck="false">/g,
            '<pre class="ql-syntax" spellcheck="false">'
        )
        .replace(/<\/htmlcode>/g, "</pre>")
        .replace(/<p class="mb-0">/g, "<p>")
        .replace(/<p class="mb-0"><br\/><\/p> /g, "<p><br/></p>");

    return oldHTML;
};

export const getCombinedList = (settings, values, code) => {
    const filteredSettings = settings?.filter((list) => list.settingCode == code);
    const settingValue = filteredSettings[0]?.value?.split(",");
    const settingId = filteredSettings[0]?.settingOptionId;

    if (settingValue?.length > 0) {
        const result = values?.filter(
            (item) =>
                settingValue.includes(item?.settingOptionId) ||
                item?.settingOptionId == settingId
        );

        if (result?.length > 0) {
            return result;
        }
    }
    return [];
};

export const getFilteredList = (settings, key, code) => {
    if (isArray(settings)) {
        const temp = code?.split(",");
        return settings.filter((l) => temp.includes(l[key]));
    }
    return [];
};

export const getServiceCodes = (arr, code) => {
    if (!Array.isArray(arr)) return [];

    return arr
        .filter(
            (item) => item?.workFlowStepCode?.toLowerCase() === code?.toLowerCase()
        )
        .flatMap((item) =>
            item?.code
                ?.split(",")
                .map((code) => code.trim())
                .filter(Boolean)
        );
};

export const stripTags = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
};

export const isWorkFlowEnabled = (arr, code) => {
    if (!Array.isArray(arr) || arr.length === 0) return "";

    const temp = arr.find((item) => item?.workFlowStepCode == code);

    return temp?.code || "";
};

// The patient-table id behind a member/family-member id -- a different identifier from the member
// id used throughout the rest of the intake flow, needed by the patienthealthgroupbycode reads.
// Mirrors Create_HealthProfile.js's own findFamilyMember(fmid)?.patientId ?? patientInfo?.id, shared
// so every prefetch trigger site resolves it the same way instead of drifting.
//
// A caller with no familyMemberId at all is unambiguously "self" -- always patientInfo?.id. But a
// caller that DOES have one can't tell "genuinely not a family member" apart from "contextFamilyList
// just hasn't loaded yet" (some prefetch trigger sites fire this close to mount, before
// profileContext's own family-list fetch settles) -- falling back to patientInfo?.id in the latter
// case would silently prefetch under the wrong (self) patientId for what may be a family-member
// booking. So that fallback is only trusted once the list has actually loaded; while still
// null/loading, this returns undefined and the caller's own patientId-guarded warm is skipped
// entirely rather than warmed under a wrong id -- a missed prefetch, never a wrong one.
export const resolvePatientId = (contextFamilyList, patientInfo, familyMemberId) => {
    if (!familyMemberId) return patientInfo?.id;

    if (!Array.isArray(contextFamilyList)) return undefined;

    const familyMember = contextFamilyList.find((m) => m?.id == familyMemberId);
    return familyMember?.patientId ?? patientInfo?.id;
};

export const isTimeLessThan = (dateTime, hours) => {
    const convertDatetime = (datetime) => {
        return new Date(datetime + "Z");
    };
    const givenDateTime = convertDatetime(dateTime);
    const currentTime = new Date();

    if (hours) {
        const thresholdTime = new Date(
            currentTime.getTime() - hours * 60 * 60 * 1000
        );
        return thresholdTime < givenDateTime;
    } else {
        return currentTime < givenDateTime;
    }
};

export const getClientIP = async () => {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data.ip;
    } catch (error) {
        return "";
    }
};

export const getAgeValidation = (d1, d2 = new Date()) => {
    if (!d1) return null;

    const birthDate = new Date(d1);
    const compareDate = new Date(d2);

    let age = compareDate.getFullYear() - birthDate.getFullYear();

    // If birthday hasn't occurred yet this year, subtract 1
    const hasHadBirthday =
        compareDate.getMonth() > birthDate.getMonth() ||
        (compareDate.getMonth() === birthDate.getMonth() &&
            compareDate.getDate() >= birthDate.getDate());

    if (!hasHadBirthday) {
        age -= 1;
    }
    return age;
};

// Maximum-age DOB bound: the earliest allowed date of birth is a rolling
// "today − maxYears years" (e.g. on 14 Jul 2026 with maxYears 125 the earliest
// valid DOB is 14 Jul 1901) — never a fixed year like 1900, which goes stale.
// Returns true when dob is on/after that earliest date.
export const isDobWithinMaxAge = (dob, maxYears) => {
    if (!dob) return true;
    const earliest = new Date();
    earliest.setFullYear(earliest.getFullYear() - maxYears);
    earliest.setHours(0, 0, 0, 0);
    return new Date(dob) >= earliest;
};

// A date of birth can never be in the future (pair with ResponseCode 680).
// Validated here rather than via the picker's maxDate: react-datepicker silently
// ignores typed out-of-bounds dates, which would leave a stale committed value
// behind text like "09/09/9999" with no error shown.
export const isDobNotInFuture = (dob) => {
    if (!dob) return true;
    return new Date(dob) <= new Date();
};

export const setSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error("Error setting session storage:", error);
    }
};

export const getSessionStorage = (key) => {
    try {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    } catch (error) {
        console.error("Error getting session storage:", error);
        return null;
    }
};

/**
 * Base64URL-encode a JSON-serializable value for safe use in a URL query param.
 * Uses the URL-safe alphabet (-, _) and strips "=" padding, so the value contains
 * only characters that survive URLSearchParams read/re-append cycles. Standard
 * base64 "+" would otherwise be turned into a space by form-urlencoded decoding.
 */
export const encodeUrlParam = (value) =>
    btoa(JSON.stringify(value)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/**
 * An SSO product's refillCount, read case-insensitively ("refillCount", "RefillCount", ...) like the API's JSON binding
 * and the SSO login check. Undefined when the product has none.
 */
export const getRefillCount = (product) => {
    if (!product || typeof product !== "object") return undefined;
    const key = Object.keys(product).find((k) => k.toLowerCase() === "refillcount");
    return key === undefined ? undefined : product[key];
};

/** SSO params.parentappointmentId, matched case-insensitively like the API's verifyuseridentity check. */
export const getParentAppointmentId = (params) => {
    if (!params || typeof params !== "object") return undefined;
    const key = Object.keys(params).find((k) => k.toLowerCase() === "parentappointmentid");
    return key === undefined ? undefined : params[key];
};

/** Inverse of encodeUrlParam — restores the standard base64 alphabet/padding, then parses. */
export const decodeUrlParam = (value) => {
    if (!value) return null;
    let b64 = value.replace(/-/g, "+").replace(/_/g, "/");
    if (b64.length % 4) b64 += "=".repeat(4 - (b64.length % 4));
    return JSON.parse(atob(b64));
};

/** Derives up-to-two-letter uppercase initials from a display name for avatar circles. */
export const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

// Core_LangResource key for the "duplicate transaction" toast — resolve it via
// getLanguageContent(DUPLICATE_TX_LANG_KEY) at the call site rather than a hardcoded string.
export const DUPLICATE_TX_LANG_KEY = "pmtdup";

// Normalises a failed-payment response. The underlying gateway error can arrive
// either as an already-deserialised envelope (res.data.data) or as a stringified
// JSON in res.data.message / res.data.errorMessage. This parses whichever is
// present and surfaces the gateway's own message plus a duplicate-charge flag,
// so every payment branch handles DUPLICATE_TRANSACTION the same way.
//
// Previously this was duplicated between PaymentGateway.js and UpdatePayment.js —
// both now import it from here so there is exactly one implementation.
export const parsePaymentError = (res) => {
    const raw = res?.data?.message || res?.data?.errorMessage || res?.data?.errormessage || "{}";
    let error;
    try { error = typeof raw === "object" ? raw : JSON.parse(raw); }
    catch { error = { messageCode: ["382"] }; }
    const gatewayMessage = res?.data?.data?.message || error?.data?.message || null;
    return { error, gatewayMessage, isDuplicate: gatewayMessage === "DUPLICATE_TRANSACTION" };
};

// Shared raw-Bootstrap info-icon tooltip. This codebase does not use react-bootstrap/
// OverlayTrigger anywhere — every tooltip is a plain SVG icon wired up via useRef + the
// Bootstrap JS `Tooltip` class (previously duplicated across PaymentGateway.js and
// UpdatePayment.js for the CVV info icon). Centralising it here so new call sites
// (e.g. the Past Due / Suspended status tooltips on the Subscription page) don't fork
// the pattern again.
//
// `html`: pass true when `text` may contain a literal "\n" (e.g. a multi-line
// Core_LangResource value) — native Bootstrap tooltips render their content as plain
// text by default, so an embedded newline is silently collapsed. When `html` is true,
// this component converts "\n" to "<br>" and tells Bootstrap to render the tooltip
// content as HTML so the line break actually shows.
export const InfoTooltip = ({
    text,
    html = false,
    placement = "top",
    className = "ms-1 text-muted",
    width = 16,
    height = 16,
}) => {
    const iconRef = useRef(null);
    const tooltipInstance = useRef(null);
    const content = html ? (text || "").replace(/\n/g, "<br>") : text;

    useEffect(() => {
        if (iconRef.current) {
            tooltipInstance.current = new Tooltip(iconRef.current, {
                trigger: "hover focus",
                placement,
                html,
            });
        }
        return () => {
            if (tooltipInstance.current) {
                tooltipInstance.current.dispose();
                tooltipInstance.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, html, placement]);

    if (!text) return null;

    return (
        <svg
            width={width}
            height={height}
            className={className}
            ref={iconRef}
            data-bs-toggle="tooltip"
            data-bs-placement={placement}
            title={content}
            role="img"
            aria-label="Info"
        >
            <use xlinkHref="#svg_info" />
        </svg>
    );
};